import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../gyzmo-commons/dao/db/base/db.dao.base";
import { isNullOrEmpty } from "../../../gyzmo-commons/helpers/null.helper";
import { AppSqlProvider } from "../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../gyzmo-commons/services/logs/logger.service";
import { MaintenanceControl } from "../../models/maintenanceControl.model";

@Injectable({
    providedIn: "root",
})
export class MaintenanceControlsDbDao extends DbDaoBase<MaintenanceControl> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider,
    ) {
        super(logger);
    }

    public async createIndexes(): Promise<void> {
        const query = `CREATE INDEX IF NOT EXISTS idx_${ MaintenanceControl.TABLENAME }_equipmentId ON ${ MaintenanceControl.TABLENAME }(equipmentId);`;
        await this.sqlProvider.query(query, []).catch(reason => {
            this.logSqlError(reason);
        });
    }

    createTable(): Promise<void> {
        const query = `CREATE TABLE IF NOT EXISTS ${ MaintenanceControl.TABLENAME } (
            id TEXT PRIMARY KEY,
            internalId TEXT,
            wording TEXT,
            equipmentId TEXT,
            plannedMinDate TEXT,
            plannedDate TEXT,
            plannedMaxDate TEXT,
            _order NUMERIC
        );`;
        return this.sqlProvider.query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    delete(id: string): Promise<any> {
        const query = `DELETE FROM ${ MaintenanceControl.TABLENAME } WHERE id = ?;`;
        const params = [id];
        return this.sqlProvider.query(query, params);
    }

    deleteAll(): Promise<any> {
        const query = `DELETE FROM ${ MaintenanceControl.TABLENAME };`;
        return this.sqlProvider.query(query, []);
    }

    get(id: string, hydrate: boolean = false): Promise<MaintenanceControl> {
        if (isNullOrEmpty(id)) {
            return Promise.resolve(null);
        }
        const query = `SELECT * FROM ${ MaintenanceControl.TABLENAME } WHERE id = ?;`;
        const params = [id];
        return this.sqlProvider.query(query, params)
            .then(data => {
                if (data.rows.length <= 0) {
                    return null;
                }
                return this.rowToModel(data.rows[0]);
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public getTableName(): string {
        return MaintenanceControl.TABLENAME;
    }

    protected rowToModel(row: any): MaintenanceControl {
        const maintenanceControl = new MaintenanceControl();
        maintenanceControl.id = row.id;
        maintenanceControl.internalId = row.internalId;
        maintenanceControl.wording = row.wording;
        maintenanceControl.equipmentId = row.equipmentId;
        maintenanceControl.plannedMinDate = row.plannedMinDate;
        maintenanceControl.plannedDate = row.plannedDate;
        maintenanceControl.plannedMaxDate = row.plannedMaxDate;
        maintenanceControl.order = row.order;
        return maintenanceControl;
    }

    save(maintenanceControl: MaintenanceControl): Promise<MaintenanceControl> {
        const query = `INSERT OR REPLACE INTO ${ MaintenanceControl.TABLENAME }
            (id, internalId, wording, equipmentId, plannedMinDate, plannedDate, plannedMaxDate, _order)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?);`;
        const params = [
            maintenanceControl.id,
            maintenanceControl.internalId,
            maintenanceControl.wording,
            maintenanceControl.equipmentId,
            maintenanceControl.plannedMinDate,
            maintenanceControl.plannedDate,
            maintenanceControl.plannedMaxDate,
            maintenanceControl.order,
        ];
        return this.sqlProvider.query(query, params)
            .then(() => {
                return maintenanceControl;
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public listForEquipment(equipmentId: string): Promise<MaintenanceControl[]> {
        const query = `SELECT * FROM ${ MaintenanceControl.TABLENAME } WHERE equipmentId = ? ORDER BY _order;`;
        const params = [equipmentId];
        return this.sqlProvider.query(query, params)
            .then(data => {
                const result: MaintenanceControl[] = [];
                for (const row of data.rows) {
                    result.push(this.rowToModel(row));
                }
                return result;
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }
}
